import { useAuth0 } from '../hooks/useAuth0';
import { useState } from 'react';
import { Button, Paper, Typography, Box, Alert } from '@mui/material';

const TestRoles = () => {
  const { getAccessTokenSilently, logout, loginWithRedirect } = useAuth0();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState(null);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    logout({ 
      logoutParams: { 
        returnTo: window.location.origin 
      } 
    });
  };

  const handleLogin = async () => {
    await loginWithRedirect({
      authorizationParams: {
        audience: 'https://clinicaltrials.api',
        prompt: 'consent',  // Force consent
      },
      appState: {
        returnTo: '/test-roles',
      },
    });
  };

  const checkToken = async () => {
    setError(null);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: 'https://clinicaltrials.api',
        },
        cacheMode: 'off',
        ignoreCache: true,
      });

      // Decode token
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1]));

      setTokenInfo({
        email: payload['https://clinicaltrials.api/email'],
        name: payload['https://clinicaltrials.api/name'],
        roles: payload['https://clinicaltrials.api/roles'] || 'No roles found',
        permissions: payload['https://clinicaltrials.api/permissions'] || 'No permissions found',
        audience: payload['aud'],
        issuer: payload['iss'],
        rawPayload: payload
      });

      console.log('📋 Token Payload:', payload);
    } catch (err) {
      console.error('Error getting token:', err);
      setError(err.message);
      
      if (err.message.includes('consent')) {
        setTokenInfo({ 
          error: 'Consent Required', 
          message: 'Please logout and login again to grant API access' 
        });
      }
    }
  };

  return (
    <Paper sx={{ p: 3, m: 3, maxWidth: 800 }}>
      <Typography variant="h5" gutterBottom>
        🔐 Token & Roles Test
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
        <Button 
          variant="contained" 
          onClick={checkToken}
        >
          Check My Token & Roles
        </Button>

        <Button 
          variant="outlined" 
          color="warning"
          onClick={handleLogout}
        >
          Logout & Clear Cache
        </Button>

        <Button 
          variant="outlined" 
          color="success"
          onClick={handleLogin}
        >
          Login with Consent
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          <strong>Error:</strong> {error}
          <br />
          <strong>Solution:</strong> Click "Logout & Clear Cache", then click "Login with Consent"
        </Alert>
      )}

      {tokenInfo && (
        <Box sx={{ mt: 2 }}>
          {tokenInfo.error ? (
            <Alert severity="warning">
              <Typography><strong>{tokenInfo.error}</strong></Typography>
              <Typography>{tokenInfo.message}</Typography>
              <Typography sx={{ mt: 1 }}>
                Click "Logout & Clear Cache" then "Login with Consent" to fix this.
              </Typography>
            </Alert>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                ✅ Token obtained successfully!
              </Alert>

              <Typography><strong>📧 Email:</strong> {tokenInfo.email || 'Not found'}</Typography>
              <Typography><strong>👤 Name:</strong> {tokenInfo.name || 'Not found'}</Typography>
              <Typography><strong>🎭 Roles:</strong> {JSON.stringify(tokenInfo.roles)}</Typography>
              <Typography><strong>🔐 Permissions:</strong> {JSON.stringify(tokenInfo.permissions)}</Typography>
              <Typography><strong>🎯 Audience:</strong> {tokenInfo.audience}</Typography>
              <Typography><strong>🏢 Issuer:</strong> {tokenInfo.issuer}</Typography>
              
              <Typography variant="h6" sx={{ mt: 2 }}>Full Token Payload:</Typography>
              <pre style={{ 
                background: '#f5f5f5', 
                padding: '10px', 
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {JSON.stringify(tokenInfo.rawPayload, null, 2)}
              </pre>
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default TestRoles;
